import React from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

import { UiText, Button } from "../components"
import ReadLink from "../components/read-link"
import gemmThumb from "../images/projects/gemm_thumb.svg"
import catFlagship from "../images/projects/cat_flagship.svg"
import catAnalog from "../images/projects/cat_analog.svg"
import catDigital from "../images/projects/cat_digital.svg"
import catArch from "../images/projects/cat_arch.svg"
import catEmbedded from "../images/projects/cat_embedded.svg"
import catTools from "../images/projects/cat_tools.svg"

const LINKEDIN = "https://www.linkedin.com/in/souleymanedembele"

// Project showcase data. Each project is a media-rich case study, not just a
// repo link: cards lead with a diagram or thumbnail and only link to code when
// the repository is already public. Private/thesis/startup work is documented
// here without exposing source.
const CATEGORIES = [
  "Master's Thesis",
  "Analog / RF IC Design",
  "Digital Design & FPGA",
  "Computer Architecture & Systems Software",
  "Embedded Systems & Control",
  "Tools & Applied Computing",
]

const projects = [
  {
    title: "GEMM Accelerator on Cyclone V SoC",
    category: "Master's Thesis",
    featured: true,
    image: gemmThumb,
    blurb:
      "Custom RTL matrix-multiply accelerator taken from SystemVerilog through Qsys integration to a full Linux bring-up (custom kernel, device-tree overlay, UIO binding, and user-space driver). An 8x8 heterogeneous systolic array runs INT8/INT16/INT32/FP16/FP32 in one multi-precision MAC. Fitter shows 112/112 DSP at 48% ALMs; a wide-port DMA fast path, operand-locality tuning (resident A, double-buffered B), and a hardware tile-row batch sequencer walk memory stall down 93% to 76% to 19% and take INT8 512^3 to 3.15 GOP/s at 50 MHz, about 27x an ARM A9 software reference, at 60% compute occupancy, bit-exact across all precisions (60/60 on-board tests).",
    tags: ["SystemVerilog", "FPGA", "Cyclone V SoC", "Linux driver", "DSP"],
    writeup: "/projects/gemm-accelerator-cyclonev",
  },
  {
    title: "CMOS Power Amplifier for GSM",
    category: "Analog / RF IC Design",
    featured: true,
    image: null,
    blurb:
      "Two-stage Class-AB power amplifier in TSMC 0.18um RF CMOS, designed in Cadence Virtuoso/SpectreRF with load-pull optimization. Targets the commercial Qorvo RF5110G and, in simulation, exceeds it: +38 dBm PSAT, 64.2% peak PAE, 33.2 dB gain, Ft of 26 GHz, with a 57% projected die-cost reduction versus GaAs HBT. (Simulation results, not silicon-measured.)",
    tags: ["RF CMOS", "Cadence Virtuoso", "SpectreRF", "Load-pull", "0.18um"],
    writeup: "/projects/cmos-power-amplifier",
  },
  {
    title: "CMOS Ring Oscillator (0.18um)",
    category: "Analog / RF IC Design",
    image: null,
    blurb:
      "Five-stage transistor-level ring oscillator in the IBM 0.18um (cmrf7sf) CMOS process, designed, simulated, and laid out in Cadence Virtuoso. Met the 1 GHz +/- 100 MHz target (measured 1.06 GHz at N=5, Wn=82um, Wp=264um, Vdd=1.33V) with a DRC/LVS-clean layout.",
    tags: ["0.18um CMOS", "Cadence Virtuoso", "DRC/LVS", "Layout", "Transient sim"],
    writeup: "/projects/ring-oscillator",
  },
  {
    title: "NES Emulator on FPGA",
    category: "Digital Design & FPGA",
    featured: true,
    image: null,
    blurb:
      "Cycle-aware NES emulator in Verilog built around a 6502 core, with custom PPU, APU, and VGA modules for video and audio. A UART path loads game ROMs and reads joystick input; memory-mapping logic handles cartridge data. Verified in Vivado/ISE and ModelSim for sprite rendering, frame rate, and audio sync.",
    tags: ["Verilog", "6502", "PPU/APU", "VGA", "Vivado"],
    writeup: "/projects/nes-fpga",
  },
  {
    title: "SystemVerilog Programmable Processor",
    category: "Digital Design & FPGA",
    image: null,
    blurb:
      "Programmable CPU in SystemVerilog (TCES330 course project). A control unit (instruction memory, program counter, instruction register, and FSM) drives a datapath (register file, ALU, data memory, and a 2:1 mux), tied together in a top-level processor. Executes NOOP/STORE/LOAD/ADD/SUB/HALT, runs on an FPGA with 7-segment output, and is verified with module and processor testbenches.",
    tags: ["SystemVerilog", "CPU", "FSM", "FPGA", "Verification"],
    writeup: "/projects/programmable-processor",
  },
  {
    title: "Three-Level Cache Simulator",
    category: "Computer Architecture & Systems Software",
    featured: true,
    image: null,
    blurb:
      "C++ simulator for an L1/L2/L3 hierarchy with configurable associativity, line size, and replacement policy (LRU/FIFO). Compared inclusive vs non-inclusive policies and a 12-entry victim cache on real trace files: improved AMAT from 7.64 to 4.80 cycles and L2 hit rate from ~23% to ~57%.",
    tags: ["C++", "Caches", "AMAT", "Trace-driven", "LRU/FIFO"],
    writeup: "/projects/three-level-cache-simulator",
  },
  {
    title: "Game Boy Advance Emulator",
    category: "Computer Architecture & Systems Software",
    image: null,
    blurb:
      "Emulator in C targeting a cycle-accurate ARM7TDMI core, written to deepen computer-architecture and systems-programming skills after building Game Boy and Game Boy Color emulators.",
    tags: ["C", "ARM7TDMI", "Emulation", "Cycle-accurate"],
    writeup: "/projects/gba-emulator",
  },
  {
    title: "Linux Kernel I2C Driver",
    category: "Computer Architecture & Systems Software",
    image: null,
    blurb:
      "Kernel-space board support and an I2C driver for a Raspberry Pi peripheral, exercised from user space through syscalls. Hands-on practice with the Linux driver model, device interfaces, and the kernel/user boundary.",
    tags: ["Linux kernel", "I2C", "BSP", "Device drivers", "C"],
    writeup: "/projects/linux-i2c-driver",
  },
  {
    title: "Quadcopter Flight Controller Firmware",
    category: "Embedded Systems & Control",
    featured: true,
    image: null,
    blurb:
      "Bare-metal firmware on an STM32F405 (ARM Cortex-M4) with a custom lightweight RTOS and ExpressLRS (LoRa) telemetry. A dual-rate lifted-model GPC state-space controller fuses an ICM20602 (1200 Hz) and a BNO085 (400 Hz) through a Kalman filter; in MATLAB/Simulink it roughly halved RMS tracking error versus a double/inner-loop PID (0.06 to 0.03) with faster settling (0.8 s vs 1.2 s) and lower overshoot (5% vs 12%). Brought up on a custom v1 PCB.",
    tags: ["STM32", "Cortex-M4", "Custom RTOS", "Kalman filter", "GPC control"],
    writeup: "/projects/flight-controller",
  },
  {
    title: "Model Predictive Control Rover",
    category: "Embedded Systems & Control",
    image: null,
    blurb:
      "Autonomous rover using sonar and an MPU6050 IMU with real-time Model Predictive Control for obstacle avoidance. STM32 (custom RTOS) drives the motors while an ESP32 (FreeRTOS) runs the Hildreth constrained-optimization solver and a Wi-Fi telemetry server, cutting avoidance response time 30% over the prior PID rover.",
    tags: ["STM32", "ESP32", "MPC", "Hildreth", "FreeRTOS"],
    writeup: "/projects/mpc-rover",
  },
  {
    title: "Wildlife UWB Tracking (Senior Capstone)",
    category: "Embedded Systems & Control",
    image: null,
    blurb:
      "Capstone with the Washington Department of Fish and Wildlife and Texas Instruments: custom ultra-wideband PCBs track endangered turtles, and a relay device computes position and texts biologists when a turtle leaves its pond. PCB design mentored by TI engineers in Altium, replacing manual radio triangulation.",
    tags: ["UWB", "Custom PCB", "Altium", "Embedded", "WDFW + TI"],
    writeup: "/projects/wildlife-uwb-tracking",
  },
  {
    title: "Autonomous Obstacle-Avoidance Rover",
    category: "Embedded Systems & Control",
    image: null,
    blurb:
      "Team rover on a TI TM4C (ARM Cortex-M4) that uses sonar to detect obstacles within a meter and steers around them. Bluetooth/UART control and PWM motor drive, developed and tested in Keil uVision.",
    tags: ["TM4C", "Cortex-M4", "Sonar", "PWM", "Keil"],
    writeup: "/projects/autonomous-rover",
  },
  {
    title: "vSchem Schematic Editor",
    category: "Tools & Applied Computing",
    image: null,
    blurb:
      "Cross-platform engineering notepad and schematic editor on a Vulkan/WebGPU C++ core. Combines freehand ink notes with drag-and-drop circuit design and KCL/KVL/MNA analysis, targeting iPad, Android, web, and desktop.",
    tags: ["C++", "Vulkan/WebGPU", "MNA solver", "EDA", "Cross-platform"],
    writeup: "/projects/vschem",
  },
  {
    title: "Image Filtering Techniques",
    category: "Tools & Applied Computing",
    image: null,
    blurb:
      "Comparative study of Gaussian blur, median filtering, and Sobel edge detection in C++ with OpenCV, analyzed in MATLAB. Measured noise reduction, edge preservation, and runtime trade-offs to guide filter selection.",
    tags: ["C++", "OpenCV", "MATLAB", "DSP", "Image processing"],
    writeup: "/projects/image-filter",
  },
]

// Deterministic gradient per category for the placeholder thumbnail used when a
// project has no diagram/photo yet.
const CATEGORY_GRADIENT = {
  "Master's Thesis": "linear-gradient(135deg, #006d77 0%, #0a9396 100%)",
  "Analog / RF IC Design": "linear-gradient(135deg, #9b2226 0%, #ca6702 100%)",
  "Digital Design & FPGA": "linear-gradient(135deg, #005f73 0%, #0a9396 100%)",
  "Computer Architecture & Systems Software":
    "linear-gradient(135deg, #3d348b 0%, #7678ed 100%)",
  "Embedded Systems & Control":
    "linear-gradient(135deg, #344e41 0%, #588157 100%)",
  "Tools & Applied Computing":
    "linear-gradient(135deg, #14213d 0%, #4a4e69 100%)",
}

// Default placeholder thumbnail per category, used until a project gets its own
// image. To add a real thumbnail: import it and set `image` on the project.
const CATEGORY_DEFAULT_IMAGE = {
  "Master's Thesis": catFlagship,
  "Analog / RF IC Design": catAnalog,
  "Digital Design & FPGA": catDigital,
  "Computer Architecture & Systems Software": catArch,
  "Embedded Systems & Control": catEmbedded,
  "Tools & Applied Computing": catTools,
}

const ProjectRow = ({ project }) => {
  const requestUrl = project.requestAccess ? LINKEDIN : null
  // Title links to the case study if there is one, else to public code.
  const titleHref = project.writeup || project.repo || null
  const titleInternal = Boolean(project.writeup)
  const thumbSrc =
    project.image || CATEGORY_DEFAULT_IMAGE[project.category] || catFlagship

  const thumb = (
    <Thumb gradient={CATEGORY_GRADIENT[project.category]}>
      <ThumbImg src={thumbSrc} alt={project.title} />
    </Thumb>
  )

  return (
    <Row>
      {titleInternal ? <Link to={titleHref}>{thumb}</Link> : thumb}
      <Content>
        <RowTitle>
          {titleHref ? (
            titleInternal ? (
              <Link to={titleHref}>{project.title}</Link>
            ) : (
              <a href={titleHref} target="_blank" rel="noopener noreferrer">
                {project.title}
              </a>
            )
          ) : (
            project.title
          )}
        </RowTitle>
        <UiText variant="content" as="p" margin="0px">
          {project.blurb}
        </UiText>
        <UiText variant="subContent" as="p" margin="0px">
          {project.tags.join("  ·  ")}
        </UiText>
        {project.writeup && (
          <ReadLink to={project.writeup}>read the case study &rarr;</ReadLink>
        )}
        {!project.writeup && project.repo && (
          <ReadLink as="a" href={project.repo} target="_blank" rel="noopener noreferrer">
            view the code &rarr;
          </ReadLink>
        )}
        {!project.writeup && !project.repo && requestUrl && (
          <ReadLink as="a" href={requestUrl} target="_blank" rel="noopener noreferrer">
            private project: request access &rarr;
          </ReadLink>
        )}
        {project.doc && (
          <ReadLink
            as="a"
            href={project.doc}
            target="_blank"
            rel="noopener noreferrer"
          >
            view the design report (PDF) &rarr;
          </ReadLink>
        )}
      </Content>
    </Row>
  )
}

const Projects = ({ limit }) => {
  // Limited (home teaser): flat list of the first N projects + a view-all link.
  if (limit) {
    return (
      <div>
        {projects.slice(0, limit).map(p => (
          <ProjectRow key={p.title} project={p} />
        ))}
        <Link to="/projects">
          <Button
            css={css`
              margin: 1rem 0 0;
              padding: 8px 16px;
            `}
          >
            <UiText as="span" margin="0px" variant="subContentBold">
              View all projects
            </UiText>
          </Button>
        </Link>
      </div>
    )
  }

  // Full page: grouped by category.
  return (
    <div>
      {CATEGORIES.map(category => {
        const items = projects.filter(p => p.category === category)
        if (items.length === 0) return null
        return (
          <section key={category}>
            <CategoryHeading>{category}</CategoryHeading>
            {items.map(p => (
              <ProjectRow key={p.title} project={p} />
            ))}
          </section>
        )
      })}
    </div>
  )
}

const CategoryHeading = styled.h3`
  margin: 2rem 0 0.5rem;
  color: var(--f-color-brandPrimary);
`
const Row = styled.article`
  border-bottom: 1px solid #ddd;
  display: flex;
  margin-top: 1rem;
  padding-bottom: 1rem;
  align-items: flex-start;
  max-width: 960px;
`
const Thumb = styled.div`
  background: ${props => props.gradient};
  margin: 0 1rem 0 0;
  width: 100px;
  height: 100px;
  flex: 0 0 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`
const ThumbImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  margin: 0;
`
const Content = styled.div`
  flex: 1;
`
const RowTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 0.25rem;
  color: var(--f-color-brandPrimary);
  a {
    color: var(--f-color-brandPrimary);
  }
`

export default Projects
